import PropTypes from 'prop-types';
import React from 'react';
import { Field } from 'react-final-form';
import {
  DateField,
  DurationField,
  RadioField,
} from '@unite-us/app-components';
import { useIsClientsDrawer } from '../../context/InteractionsContext';
import {
  FIELD_NAMES,
  LABEL_CLASSNAME,
  PERIOD_TYPE,
  PERIOD_OF_SERVICE_OPTIONS,
} from '../../constants';
import { getMinServiceDate, getMaxServiceDate } from '../../utils/periodOfServiceDateBounds';

const toFormDateValue = (momentValue) => momentValue.utc().format('MM/DD/YYYY');

const getDateStyle = (isClientsDrawer) => (
  isClientsDrawer ?
    {
      container: { width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
      input: {
        width: '100%', maxWidth: '100%', height: '38px', boxSizing: 'border-box',
      },
      dropdown: { left: '50%', right: 'auto', transform: 'translateX(-50%)' },
    } :
    {
      container: { width: '60%', maxWidth: '60%', boxSizing: 'border-box' },
      input: {
        width: '100%', maxWidth: '100%', height: '38px', boxSizing: 'border-box',
      },
      dropdown: { right: 0, left: 'auto' },
    }
);

const getRangeStyle = (isClientsDrawer) => (
  isClientsDrawer ?
    {
      container: { width: '100%', maxWidth: '100%' },
      dropdown: { left: '50%', right: 'auto', transform: 'translateX(-50%)' },
    } :
    {
      container: { width: '60%', maxWidth: '60%' },
      dropdown: { right: 0, left: 'auto' },
    }
);

const RANGE_CLASS = 'mb-0 w-full [&_.w-64]:!w-full [&_.w-64]:!max-w-full';

const getSectionClass = (isClientsDrawer) => (
  isClientsDrawer ?
    'grid w-full grid-cols-1 gap-y-2 items-start' :
    'grid w-full grid-cols-2 gap-x-6 gap-y-2 items-start'
);

export function PeriodOfServiceRow({ periodType }) {
  const isClientsDrawer = useIsClientsDrawer();
  const maxDate = getMaxServiceDate().endOf('day').toDate();
  const minDate = getMinServiceDate().toDate();

  return (
    <div className={getSectionClass(isClientsDrawer)}>
      <div className="min-w-0" data-testid="structured-interaction-period-type">
        <Field name={FIELD_NAMES.periodType}>
          {({ input, meta }) => {
            const error = meta.touched && meta.error ? meta.error : undefined;

            return (
              <RadioField
                className="mb-0"
                error={error}
                id="structured-interaction-period-type"
                inline
                invalid={!!error}
                label="Period of Service"
                labelClassName={LABEL_CLASSNAME}
                onBlur={input.onBlur}
                onChange={input.onChange}
                options={PERIOD_OF_SERVICE_OPTIONS}
                required
                touched={meta.touched}
                value={input.value}
              />
            );
          }}
        </Field>
      </div>
      <div className="min-w-0">
        {periodType === PERIOD_TYPE.SINGLE && (
          <Field name={FIELD_NAMES.singleDate}>
            {({ input, meta }) => {
              const error = meta.touched && meta.error ? meta.error : undefined;

              return (
                <>
                  <DateField
                    className="mb-0 w-full"
                    data-testid="structured-interaction-single-date"
                    id="structured-interaction-single-date"
                    label="Single Date"
                    labelClassName={`${LABEL_CLASSNAME} app-components-form-field__label--required`}
                    onBlur={input.onBlur}
                    onChange={input.onChange}
                    required={false}
                    style={getDateStyle(isClientsDrawer)}
                    value={input.value}
                    valueFormat={toFormDateValue}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                  {error && <p className="text-red text-xs">{error}</p>}
                </>
              );
            }}
          </Field>
        )}
        {periodType === PERIOD_TYPE.RANGE && (
          <Field name={FIELD_NAMES.rangeStart}>
            {(startProps) => (
              <Field name={FIELD_NAMES.rangeEnd}>
                {(endProps) => (
                  <>
                    <label
                      htmlFor="structured-interaction-date-range"
                      className={`block mb-1 ${LABEL_CLASSNAME} app-components-form-field__label--required`}
                    >
                      Date Range
                    </label>
                    <DurationField
                      buttonClassName="'min-w-0 w-full'"
                      className={RANGE_CLASS}
                      data-testid="structured-interaction-date-range"
                      hideLabel
                      style={getRangeStyle(isClientsDrawer)}
                      id="structured-interaction-date-range"
                      isUtc
                      label="Date Range"
                      onBlur={() => {
                        startProps.input.onBlur();
                        endProps.input.onBlur();
                      }}
                      startField={{
                        ...startProps,
                        meta: {
                          ...startProps.meta,
                          error: startProps.meta.touched && startProps.meta.error ?
                            startProps.meta.error :
                            undefined,
                        },
                      }}
                      endField={{
                        ...endProps,
                        meta: {
                          ...endProps.meta,
                          error: startProps.input.value && endProps.meta.touched && endProps.meta.error ?
                            endProps.meta.error :
                            undefined,
                        },
                      }}
                      required={false}
                      valueFormat={toFormDateValue}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </>
                )}
              </Field>
            )}
          </Field>
        )}
      </div>
    </div>
  );
}

PeriodOfServiceRow.propTypes = {
  periodType: PropTypes.string.isRequired,
};
